import { CLIENT_INFO_STORAGE, KNOWN_CLIENT_ID } from "./constants.js";
import { Configuration } from "./config.js";
import PingUploadManager from "./upload/manager.js";
import { extractBooleanFromString, isBoolean, isString, sanitizeApplicationId, } from "./utils.js";
import { CoreMetrics } from "./internal_metrics.js";
import { DatetimeMetric } from "./metrics/types/datetime.js";
import CorePings from "./internal_pings.js";
import { Context } from "./context.js";
import log, { LoggingLevel } from "./log.js";
import MetricsDatabase from "./metrics/database.js";
import EventsDatabase from "./metrics/events_database/index.js";
import PingsDatabase from "./pings/database.js";
import ErrorManager from "./error/index.js";
import GleanMetrics from "./glean_metrics.js";
const LOG_TAG = "core.Glean";
var DebugOption;
(function (DebugOption) {
    DebugOption["DebugTag"] = "DebugTag";
    DebugOption["SourceTags"] = "SourceTags";
    DebugOption["LogPings"] = "LogPings";
})(DebugOption || (DebugOption = {}));
const setDebugOptionInSessionStorage = (option, value) => {
    const key = `Glean.${option.toString()}`;
    switch (option) {
        case DebugOption.DebugTag:
            sessionStorage.setItem(key, value);
            break;
        case DebugOption.LogPings:
            sessionStorage.setItem(key, value.toString());
            break;
        case DebugOption.SourceTags:
            sessionStorage.setItem(key, value.join(","));
            break;
    }
};
const getDebugOptionFromSessionStorage = (option) => {
    return sessionStorage.getItem(`Glean.${option.toString()}`) || undefined;
};
var Glean;
(function (Glean) {
    function onUploadEnabled() {
        Context.uploadEnabled = true;
        Glean.pingUploader.resumeUploads();
    }
    function onUploadDisabled(at_init) {
        let reason;
        if (at_init) {
            reason = "at_init";
        }
        else {
            reason = "set_upload_enabled";
        }
        Context.uploadEnabled = false;
        Context.corePings.deletionRequest.submit(reason);
        clearMetrics();
    }
    function clearMetrics() {
        Glean.pingUploader.clearPendingPingsQueue();
        let firstRunDate;
        try {
            firstRunDate = new DatetimeMetric(Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, Context.coreMetrics.firstRunDate)).date;
        }
        catch (_a) {
            firstRunDate = new Date();
        }
        Context.eventsDatabase.clearAll();
        Context.metricsDatabase.clearAll();
        Context.pingsDatabase.clearAll();
        Context.uploadEnabled = true;
        Context.coreMetrics.clientId.set(KNOWN_CLIENT_ID);
        Context.coreMetrics.firstRunDate.set(firstRunDate);
        Context.uploadEnabled = false;
    }
    function setDebugOptionsFromSessionStorage() {
        if (typeof window === "undefined" ||
            typeof window.sessionStorage === "undefined") {
            return;
        }
        const logPings = getDebugOptionFromSessionStorage(DebugOption.LogPings);
        if (logPings) {
            Glean.preInitLogPings = extractBooleanFromString(logPings);
        }
        const debugViewTag = getDebugOptionFromSessionStorage(DebugOption.DebugTag);
        if (debugViewTag) {
            Glean.preInitDebugViewTag = debugViewTag;
        }
        const sourceTags = getDebugOptionFromSessionStorage(DebugOption.SourceTags);
        if (sourceTags) {
            Glean.preInitSourceTags = sourceTags.split(",");
        }
    }
    function initialize(applicationId, uploadEnabled, config) {
        if (Context.initialized) {
            log(LOG_TAG, "Attempted to initialize Glean, but it has already been initialized. Ignoring.", LoggingLevel.Warn);
            return;
        }
        if (!isString(applicationId)) {
            log(LOG_TAG, "Unable to initialize Glean, applicationId must be a string.", LoggingLevel.Error);
            return;
        }
        if (!isBoolean(uploadEnabled)) {
            log(LOG_TAG, "Unable to initialize Glean, uploadEnabled must be a boolean.", LoggingLevel.Error);
            return;
        }
        if (applicationId.length === 0) {
            log(LOG_TAG, "Unable to initialize Glean, applicationId cannot be an empty string.", LoggingLevel.Error);
            return;
        }
        if (!Context.platform) {
            log(LOG_TAG, "Unable to initialize Glean, platform has not been set.", LoggingLevel.Error);
            return;
        }
        Context.coreMetrics = new CoreMetrics();
        Context.corePings = new CorePings();
        Context.applicationId = sanitizeApplicationId(applicationId);
        Context.config = new Configuration(config);
        setDebugOptionsFromSessionStorage();
        if (Glean.preInitLogPings)
            Context.config.logPings = Glean.preInitLogPings;
        if (Glean.preInitDebugViewTag)
            Context.config.debugViewTag = Glean.preInitDebugViewTag;
        if (Glean.preInitSourceTags)
            Context.config.sourceTags = Glean.preInitSourceTags;
        Context.metricsDatabase = new MetricsDatabase();
        Context.eventsDatabase = new EventsDatabase();
        Context.pingsDatabase = new PingsDatabase();
        Context.errorManager = new ErrorManager();
        Glean.pingUploader = new PingUploadManager(Context.config, Context.pingsDatabase);
        Context.initialized = true;
        Context.uploadEnabled = uploadEnabled;
        Context.eventsDatabase.initialize();
        if (uploadEnabled) {
            Context.metricsDatabase.clear("application");
            onUploadEnabled();
            Context.coreMetrics.initialize();
            if (config === null || config === void 0 ? void 0 : config.enableAutoPageLoadEvents) {
                GleanMetrics.pageLoad();
            }
            if (config === null || config === void 0 ? void 0 : config.enableAutoElementClickEvents) {
                document.addEventListener("click", (event) => {
                    GleanMetrics.handleClickEvent(event);
                });
            }
        }
        else {
            const clientId = Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, Context.coreMetrics.clientId);
            if (clientId) {
                if (clientId !== KNOWN_CLIENT_ID) {
                    onUploadDisabled(true);
                }
            }
            else {
                clearMetrics();
            }
        }
        Context.pingsDatabase.scanPendingPings();
    }
    Glean.initialize = initialize;
    function setUploadEnabled(flag) {
        if (!Context.initialized) {
            log(LOG_TAG, [
                "Changing upload enabled before Glean is initialized is not supported.\n",
                "Pass the correct state into `initialize`.\n",
                "See documentation at https://mozilla.github.io/glean/book/user/general-api.html#initializing-the-glean-sdk`",
            ], LoggingLevel.Error);
            return;
        }
        if (!isBoolean(flag)) {
            log(LOG_TAG, "Unable to change upload state, new value must be a boolean. Ignoring.", LoggingLevel.Error);
            return;
        }
        if (Context.uploadEnabled !== flag) {
            if (flag) {
                onUploadEnabled();
                Context.coreMetrics.initialize();
            }
            else {
                onUploadDisabled(false);
            }
        }
    }
    Glean.setUploadEnabled = setUploadEnabled;
    function setExperimentationId(experimentationId) {
        Context.config.experimentationId = experimentationId;
    }
    Glean.setExperimentationId = setExperimentationId;
    function setLogPings(flag) {
        if (!Context.initialized) {
            Glean.preInitLogPings = flag;
        }
        else {
            Context.config.logPings = flag;
        }
    }
    Glean.setLogPings = setLogPings;
    function setDebugViewTag(value) {
        if (!Context.initialized) {
            Glean.preInitDebugViewTag = value;
        }
        else {
            Context.config.debugViewTag = value;
        }
    }
    Glean.setDebugViewTag = setDebugViewTag;
    function setSourceTags(value) {
        if (!Context.initialized) {
            Glean.preInitSourceTags = value;
        }
        else {
            Context.config.sourceTags = value;
        }
    }
    Glean.setSourceTags = setSourceTags;
    function setPlatform(platform) {
        if (Context.initialized) {
            return;
        }
        if (Context.isPlatformSet() &&
            Context.platform.name !== platform.name &&
            !Context.testing) {
            log(LOG_TAG, [
                `IMPOSSIBLE: Attempted to change Glean's targeted platform",
            "from "${Context.platform.name}" to "${platform.name}". Ignoring.`,
            ], LoggingLevel.Error);
        }
        Context.platform = platform;
    }
    Glean.setPlatform = setPlatform;
})(Glean || (Glean = {}));
let hasStorage = false;
try {
    hasStorage = typeof window !== "undefined" &&
        typeof window.sessionStorage !== "undefined";
}
catch (e) {
    console.error("No session storage available", e);
}
if (hasStorage) {
    window.Glean = {
        setLogPings: (flag) => {
            setDebugOptionInSessionStorage(DebugOption.LogPings, flag);
            Glean.setLogPings(flag);
            console.log("Pings will be logged to the console until this tab is closed.");
        },
        setDebugViewTag: (value) => {
            setDebugOptionInSessionStorage(DebugOption.DebugTag, value);
            Glean.setDebugViewTag(value);
            console.log("Pings will be sent to the Debug Ping Viewer until this tab is closed. Pings can be found here: https://debug-ping-preview.firebaseapp.com/.");
        },
        setSourceTags: (value) => {
            setDebugOptionInSessionStorage(DebugOption.SourceTags, value);
            Glean.setSourceTags(value);
            console.log("Pings will be given the specified tags until the tab is closed.");
        },
        debugSession: () => {
            const sessionId = Context.metricsDatabase.getMetric(CLIENT_INFO_STORAGE, Context.coreMetrics.sessionId);
            if (!!sessionId && typeof sessionId === "string" && !!Context.config.debugViewTag) {
                window.open(`https://debug-ping-preview.firebaseapp.com/stream/${Context.config.debugViewTag}#${sessionId}`, "_blank");
            }
            else {
                console.info("You must set a debug tag via `window.Glean.setDebugViewTag` before debugging your session.");
            }
        }
    };
}
export default Glean;
